import {
  AddBoxOutlined,
  AssignmentOutlined,
  ClassOutlined,
  DriveEtaOutlined,
  EditOutlined,
  ExitToAppOutlined,
  GavelOutlined,
  HelpOutline,
  HomeOutlined,
  HourglassEmpty,
  PanToolOutlined,
  PersonOutlined,
  Print,
  Search,
  VisibilityOutlined,
  WorkOutline,
  SummarizeOutlined,
  ManageAccountsOutlined,
  FileDownloadOutlined,
  ManageSearchOutlined,
  AccountBalanceOutlined,
  ManageSearch,
} from "@mui/icons-material";
import { ReactNode } from "react";

import { RegisterType } from "../common/interfaces/enums/RegisterType";
import { PermissionType } from "../common/interfaces/enums/PermissionType";
import { UserType } from "../common/interfaces/enums/UserType";

export type RouteItem = {
  Path: string;
  Label: string;
  Icon?: ReactNode;
  UserTypes?: UserType[];
  Permission?: PermissionType;
};

export type Crud = {
  Root: RouteItem;
  Search: RouteItem;
  New: RouteItem;
  Edit: RouteItem;
  Preview: RouteItem;
};

export type Register = {
  Register: RegisterType;
  Print: RouteItem;
  HistoryPreview: RouteItem;
} & Crud;

type CommonRoutesConfigProps = {
  Login: RouteItem;
  LoginRoute: RouteItem;
  LoginWithPassword: RouteItem;
  AdminLogin: RouteItem;
  Logout: RouteItem;
  Help: RouteItem;
  NotFound: RouteItem;
  SSL: RouteItem;
  Forbidden: RouteItem;
  isRouteCommon: (pathname: string) => boolean;
};

export const CommonRoutesConfig: CommonRoutesConfigProps = {
  Login: { Label: "Вход", Path: "/login", Icon: <ExitToAppOutlined /> },
  LoginRoute: { Label: "Вход", Path: "/login/:uid", Icon: <ExitToAppOutlined /> },
  LoginWithPassword: { Label: "Вход", Path: "/external-login/:token", Icon: <ExitToAppOutlined /> },
  AdminLogin: { Label: "Вход", Path: "/admin/login", Icon: <ExitToAppOutlined /> },
  Logout: { Label: "Изход", Path: "/logout", Icon: <ExitToAppOutlined /> },
  Help: { Label: "Помощ", Path: "/help", Icon: <HelpOutline /> },
  Forbidden: { Label: "", Path: "/forbidden" },
  SSL: { Label: "", Path: "/ssl" },
  NotFound: { Label: "", Path: "*" },
  isRouteCommon: (pathname: string) => {
    let isCommonRoute = false;
    Object.keys(CommonRoutesConfig).forEach((prop) => {
      const path = (CommonRoutesConfig as any)[prop]["Path"];
      if (pathname.startsWith(path)) isCommonRoute = true;
    });
    return isCommonRoute;
  },
};

type PublicRoutesConfigProps = {
  Home: RouteItem;
  Profile: RouteItem;
  Authorization: Register;
  WithdrawnAuthorization: Register;
  Will: Register;
  TZCertifications: Register;
  ZOZCertifications: Register;
  MayorAuthorization: Register;
  Vehicle: {
    Waiting: RouteItem;
  } & Register;
  NapObligations: {
    Root: RouteItem;
    Request: RouteItem;
    Search: RouteItem;
    Preview: RouteItem;
    Print: RouteItem;
  };
  Report: {
    Root: RouteItem;
    Search: RouteItem;
  };
};

export const PublicRoutesConfig: PublicRoutesConfigProps = {
  Home: { Label: "Начало", Path: "/", Icon: <HomeOutlined /> },

  Profile: { Label: "Профил", Path: "/profile", Icon: <PersonOutlined /> },
  Authorization: {
    Register: RegisterType.Authorizations,
    Root: {
      Label: "Пълномощни",
      Path: "/authorizations",
      Icon: <GavelOutlined />,
      UserTypes: [UserType.Notary, UserType.NotaryDeputy, UserType.ExternalUser],
      Permission: PermissionType.Read,
    },
    Search: { Label: "Търси", Path: "/authorizations", Icon: <Search />, Permission: PermissionType.Read },
    New: { Label: "Добави", Path: "/authorizations/new", Icon: <AddBoxOutlined />, Permission: PermissionType.Create },
    Edit: {
      Label: "Редакция",
      Path: "/authorizations/edit/:id",
      Icon: <EditOutlined />,
      Permission: PermissionType.Update,
    },
    Preview: {
      Label: "Преглед",
      Path: "/authorizations/preview/:id",
      Icon: <VisibilityOutlined />,
      Permission: PermissionType.Read,
    },
    Print: { Label: "Принтирай", Path: "", Icon: <Print />, Permission: PermissionType.Read },
    HistoryPreview: { Label: "", Path: "/authorizations/history-preview/:id", Permission: PermissionType.Read },
  },
  WithdrawnAuthorization: {
    Register: RegisterType.WithdrawnAuthorizations,
    Root: {
      Label: "Оттеглени пълномощни",
      Path: "/withdrawnAuthorizations",
      Icon: <PanToolOutlined />,
      UserTypes: [UserType.Notary, UserType.NotaryDeputy, UserType.ExternalUser, UserType.BankUser],
      Permission: PermissionType.Read,
    },
    Search: { Label: "Търси", Path: "/withdrawnAuthorizations", Icon: <Search />, Permission: PermissionType.Read },
    New: {
      Label: "Добави",
      Path: "/withdrawnAuthorizations/new",
      Icon: <AddBoxOutlined />,
      Permission: PermissionType.Create,
    },
    Edit: {
      Label: "Редакция",
      Path: "/withdrawnAuthorizations/edit/:id",
      Icon: <EditOutlined />,
      Permission: PermissionType.Update,
    },
    Preview: {
      Label: "Преглед",
      Path: "/withdrawnAuthorizations/preview/:id",
      Icon: <VisibilityOutlined />,
      Permission: PermissionType.Read,
    },
    Print: {
      Label: "Принтирай",
      Path: "",
      Icon: <Print />,
      Permission: PermissionType.Read,
    },
    HistoryPreview: {
      Label: "",
      Path: "/withdrawnAuthorizations/history-preview/:id",
      Permission: PermissionType.Read,
    },
  },
  Will: {
    Register: RegisterType.Wills,
    Root: {
      Label: "Завещания",
      Path: "/wills",
      Icon: <ClassOutlined />,
      UserTypes: [UserType.Notary, UserType.NotaryDeputy, UserType.ExternalUser],
      Permission: PermissionType.Read,
    },
    Search: { Label: "Търси", Path: "/wills", Icon: <Search />, Permission: PermissionType.Read },
    New: { Label: "Добави", Path: "/wills/new", Icon: <AddBoxOutlined />, Permission: PermissionType.Create },
    Edit: { Label: "Редакция", Path: "/wills/edit/:id", Icon: <EditOutlined />, Permission: PermissionType.Update },
    Preview: {
      Label: "Преглед",
      Path: "/wills/preview/:id",
      Icon: <VisibilityOutlined />,
      Permission: PermissionType.Read,
    },
    Print: { Label: "Принтирай", Path: "", Icon: <Print />, Permission: PermissionType.Read },
    HistoryPreview: { Label: "", Path: "/wills/history-preview/:id", Permission: PermissionType.Read },
  },
  TZCertifications: {
    Register: RegisterType.TZCertifications,
    Root: {
      Label: "Удостоверявания по ТЗ",
      Path: "/certifications-tz",
      Icon: <AssignmentOutlined />,
      UserTypes: [UserType.Notary, UserType.NotaryDeputy, UserType.ExternalUser],
      Permission: PermissionType.Read,
    },
    Search: { Label: "Търси", Path: "/certifications-tz", Icon: <Search />, Permission: PermissionType.Read },
    New: {
      Label: "Добави",
      Path: "/certifications-tz/new",
      Icon: <AddBoxOutlined />,
      Permission: PermissionType.Create,
    },
    Edit: {
      Label: "Редакция",
      Path: "/certifications-tz/edit/:id",
      Icon: <EditOutlined />,
      Permission: PermissionType.Update,
    },
    Preview: {
      Label: "Преглед",
      Path: "/certifications-tz/preview/:id",
      Icon: <VisibilityOutlined />,
      Permission: PermissionType.Read,
    },
    Print: {
      Label: "Принтирай",
      Path: "",
      Icon: <Print />,
      Permission: PermissionType.Read,
    },
    HistoryPreview: { Label: "", Path: "/certifications-tz/history-preview/:id", Permission: PermissionType.Read },
  },
  ZOZCertifications: {
    Register: RegisterType.ZOZCertifications,
    Root: {
      Label: "Удостоверявания по ЗОЗ",
      Path: "/certifications-zoz",
      Icon: <SummarizeOutlined />,
      UserTypes: [UserType.Notary, UserType.NotaryDeputy, UserType.ExternalUser],
      Permission: PermissionType.Read,
    },
    Search: { Label: "Търси", Path: "/certifications-zoz", Icon: <Search />, Permission: PermissionType.Read },
    New: {
      Label: "Добави",
      Path: "/certifications-zoz/new",
      Icon: <AddBoxOutlined />,
      Permission: PermissionType.Create,
    },
    Edit: {
      Label: "Редакция",
      Path: "/certifications-zoz/edit/:id",
      Icon: <EditOutlined />,
      Permission: PermissionType.Update,
    },
    Preview: {
      Label: "Преглед",
      Path: "/certifications-zoz/preview/:id",
      Icon: <VisibilityOutlined />,
      Permission: PermissionType.Read,
    },
    Print: {
      Label: "Принтирай",
      Path: "",
      Icon: <Print />,
      Permission: PermissionType.Read,
    },
    HistoryPreview: { Label: "", Path: "/certifications-zoz/history-preview/:id", Permission: PermissionType.Read },
  },
  Vehicle: {
    Register: RegisterType.VehicleContracts,
    Root: {
      Label: "Договори МПС",
      Path: "/vehicles",
      Icon: <DriveEtaOutlined />,
      UserTypes: [UserType.Notary, UserType.NotaryDeputy],
      Permission: PermissionType.Read,
    },
    Waiting: {
      Label: "Чакащи",
      Path: "/vehicles/waiting",
      Icon: <HourglassEmpty />,
      UserTypes: [UserType.Notary, UserType.NotaryDeputy],
      Permission: PermissionType.Read,
    },
    Search: {
      Label: "Търси",
      Path: "/vehicles/search",
      Icon: <Search />,
      UserTypes: [UserType.Notary, UserType.NotaryDeputy],
      Permission: PermissionType.Read,
    },
    New: {
      Label: "Добави",
      Path: "/vehicles/waiting/new/:id",
      Icon: <AddBoxOutlined />,
      Permission: PermissionType.Create,
    },
    Edit: {
      Label: "Редакция",
      Path: "/vehicles/search/edit/:id",
      Icon: <EditOutlined />,
      Permission: PermissionType.Update,
    },
    Preview: {
      Label: "Преглед",
      Path: "/vehicles/search/preview/:id",
      Icon: <VisibilityOutlined />,
      Permission: PermissionType.Read,
    },
    Print: { Label: "Принтирай", Path: "", Icon: <Print />, Permission: PermissionType.Read },
    HistoryPreview: { Label: "", Path: "", Permission: PermissionType.Read },
  },
  NapObligations: {
    Root: {
      Label: "НАП",
      Path: "/obligations",
      Icon: <AccountBalanceOutlined />,
      UserTypes: [UserType.Notary, UserType.NotaryDeputy],
      Permission: PermissionType.Read,
    },
    Request: {
      Label: "Проверка на ЗЛ",
      Path: "/obligations/request",
      Icon: <ManageSearch />,
      UserTypes: [UserType.Notary, UserType.NotaryDeputy],
      Permission: PermissionType.Create,
    },
    Search: {
      Label: "Статус на проверка",
      Path: "/obligations/search",
      Icon: <Search />,
      UserTypes: [UserType.Notary, UserType.NotaryDeputy],
      Permission: PermissionType.Read,
    },
    Preview: {
      Label: "Преглед",
      Path: "/obligations/preview/:id",
      Icon: <VisibilityOutlined />,
      UserTypes: [UserType.Notary, UserType.NotaryDeputy],
      Permission: PermissionType.Read,
    },
    Print: { Label: "Принтирай", Path: "", Icon: <Print />, Permission: PermissionType.Read },
  },
  MayorAuthorization: {
    Register: RegisterType.Authorizations,
    Root: {
      Label: "Пълномощни",
      Path: "/mayorAuthorizations",
      Icon: <GavelOutlined />,
      UserTypes: [UserType.Notary, UserType.NotaryDeputy],
      Permission: PermissionType.Read,
    },
    Search: { Label: "Търси", Path: "/mayorAuthorizations", Icon: <Search />, Permission: PermissionType.Read },
    New: { Label: "", Path: "", Icon: <></> },
    Edit: { Label: "", Path: "", Icon: <></> },
    Preview: {
      Label: "Преглед",
      Path: "/mayorAuthorizations/preview/:id",
      Icon: <VisibilityOutlined />,
      Permission: PermissionType.Read,
    },
    Print: {
      Label: "Принтирай",
      Path: "",
      Icon: <Print />,
      Permission: PermissionType.Read,
    },
    HistoryPreview: { Label: "", Path: "/mayorAuthorizations/history-preview/:id", Permission: PermissionType.Read },
  },
  Report: {
    Root: {
      Label: "Индивидуална справка",
      Path: "/report",
      Icon: <WorkOutline />,
      UserTypes: [UserType.Notary, UserType.NotaryDeputy],
    },
    Search: { Label: "Търси", Path: "/report", Icon: <Search /> },
  },
};

type AdminRoutesConfigProps = {
  Home: RouteItem;
  Notary: Crud;
  NotaryDeputy: Crud;
  ExternalUser: Crud;
  RegistryAgencyUser: Crud;
  BankUser: Crud;
  InvestmentBrokerUser: Crud;
  InsurerUser: Crud;
  Mayor: Crud;
  Bank: Crud;
  BankBranch: Crud;
  BankAuthorizationLog: RouteItem;
  BankWithdrawnAuthorizationLog: RouteItem;
  InvestmentBroker: Crud;
  InvestmentBrokerBranch: Crud;
  Insurer: Crud;
  InsurerBranch: Crud;
  InvestmentBrokerAuthorizationLog: RouteItem;
  InvestmentBrokerWithdrawnAuthorizationLog: RouteItem;
  InsurerAuthorizationLog: RouteItem;
  InsurerWithdrawnAuthorizationLog: RouteItem;
  NapObligationLog: RouteItem;
  isRouteAdmin: (pathname: string) => boolean;
};

export const AdminRoutesConfig: AdminRoutesConfigProps = {
  isRouteAdmin: (pathname: string) => pathname.startsWith("/admin"),
  Home: { Label: "Начало", Path: "/admin/home", Icon: <HomeOutlined /> },
  Notary: {
    Root: {
      Label: "Нотариуси",
      Path: "/admin/notaries",
      Icon: <ManageAccountsOutlined />,
      UserTypes: [UserType.Admin],
    },
    Search: { Label: "Търси", Path: "/admin/notaries", Icon: <Search /> },
    New: { Label: "Добави", Path: "/admin/notaries/new", Icon: <AddBoxOutlined /> },
    Edit: { Label: "Редакция", Path: "/admin/notaries/edit/:id", Icon: <EditOutlined /> },
    Preview: { Label: "Преглед", Path: "/admin/notaries/preview/:id", Icon: <VisibilityOutlined /> },
  },
  NotaryDeputy: {
    Root: {
      Label: "Помощник-нотариуси по заместване",
      Path: "/admin/notaryDeputies",
      Icon: <ManageAccountsOutlined />,
      UserTypes: [UserType.Admin],
    },
    Search: { Label: "Търси", Path: "/admin/notaryDeputies", Icon: <Search /> },
    New: { Label: "Добави", Path: "/admin/notaryDeputies/new", Icon: <AddBoxOutlined /> },
    Edit: { Label: "Редакция", Path: "/admin/notaryDeputies/edit/:id", Icon: <EditOutlined /> },
    Preview: { Label: "Преглед", Path: "/admin/notaryDeputies/preview/:id", Icon: <VisibilityOutlined /> },
  },
  ExternalUser: {
    Root: {
      Label: "Външни потребители",
      Path: "/admin/externalUsers",
      Icon: <ManageAccountsOutlined />,
      UserTypes: [UserType.Admin],
    },
    Search: { Label: "Търси", Path: "/admin/externalUsers", Icon: <Search /> },
    New: { Label: "Добави", Path: "/admin/externalUsers/new", Icon: <AddBoxOutlined /> },
    Edit: { Label: "Редакция", Path: "/admin/externalUsers/edit/:id", Icon: <EditOutlined /> },
    Preview: { Label: "Преглед", Path: "/admin/externalUsers/preview/:id", Icon: <VisibilityOutlined /> },
  },
  RegistryAgencyUser: {
    Root: {
      Label: "Потребители на АВ",
      Path: "/admin/registryAgencyUsers",
      Icon: <ManageAccountsOutlined />,
      UserTypes: [UserType.Admin],
    },
    Search: { Label: "Търси", Path: "/admin/registryAgencyUsers", Icon: <Search /> },
    New: { Label: "Добави", Path: "/admin/registryAgencyUsers/new", Icon: <AddBoxOutlined /> },
    Edit: { Label: "Редакция", Path: "/admin/registryAgencyUsers/edit/:id", Icon: <EditOutlined /> },
    Preview: { Label: "Преглед", Path: "/admin/registryAgencyUsers/preview/:id", Icon: <VisibilityOutlined /> },
  },
  BankUser: {
    Root: {
      Label: "Потребители на банка",
      Path: "/admin/bankUsers",
      Icon: <ManageAccountsOutlined />,
      UserTypes: [UserType.Admin],
    },
    Search: { Label: "Търси", Path: "/admin/bankUsers", Icon: <Search /> },
    New: { Label: "Добави", Path: "/admin/bankUsers/new", Icon: <AddBoxOutlined /> },
    Edit: { Label: "Редакция", Path: "/admin/bankUsers/edit/:id", Icon: <EditOutlined /> },
    Preview: { Label: "Преглед", Path: "/admin/bankUsers/preview/:id", Icon: <VisibilityOutlined /> },
  },
  InvestmentBrokerUser: {
    Root: {
      Label: "Инв. посредници",
      Path: "/admin/investmentBrokerUsers",
      Icon: <ManageAccountsOutlined />,
      UserTypes: [UserType.Admin],
    },
    Search: { Label: "Търси", Path: "/admin/investmentBrokerUsers", Icon: <Search /> },
    New: { Label: "Добави", Path: "/admin/investmentBrokerUsers/new", Icon: <AddBoxOutlined /> },
    Edit: { Label: "Редакция", Path: "/admin/investmentBrokerUsers/edit/:id", Icon: <EditOutlined /> },
    Preview: { Label: "Преглед", Path: "/admin/investmentBrokerUsers/preview/:id", Icon: <VisibilityOutlined /> },
  },
  InsurerUser: {
    Root: {
      Label: "Застрахователи",
      Path: "/admin/insurerUsers",
      Icon: <ManageAccountsOutlined />,
      UserTypes: [UserType.Admin],
    },
    Search: { Label: "Търси", Path: "/admin/insurerUsers", Icon: <Search /> },
    New: { Label: "Добави", Path: "/admin/insurerUsers/new", Icon: <AddBoxOutlined /> },
    Edit: { Label: "Редакция", Path: "/admin/insurerUsers/edit/:id", Icon: <EditOutlined /> },
    Preview: { Label: "Преглед", Path: "/admin/insurerUsers/preview/:id", Icon: <VisibilityOutlined /> },
  },
  Mayor: {
    Root: { Label: "Кметове", Path: "/admin/mayors", Icon: <ManageAccountsOutlined />, UserTypes: [UserType.Admin] },
    Search: { Label: "Търси", Path: "/admin/mayors", Icon: <Search /> },
    New: { Label: "Добави", Path: "/admin/mayors/new", Icon: <AddBoxOutlined /> },
    Edit: { Label: "Редакция", Path: "/admin/mayors/edit/:id", Icon: <EditOutlined /> },
    Preview: { Label: "Преглед", Path: "/admin/mayors/preview/:id", Icon: <VisibilityOutlined /> },
  },
  Bank: {
    Root: { Label: "Банки", Path: "/admin/banks", Icon: <ManageSearchOutlined />, UserTypes: [UserType.Admin] },
    Search: { Label: "Търси", Path: "/admin/banks", Icon: <Search /> },
    New: { Label: "Добави", Path: "/admin/banks/new", Icon: <AddBoxOutlined /> },
    Edit: { Label: "Редакция", Path: "/admin/banks/edit/:id", Icon: <EditOutlined /> },
    Preview: { Label: "Преглед", Path: "/admin/banks/preview/:id", Icon: <VisibilityOutlined /> },
  },
  BankBranch: {
    Root: {
      Label: "Клонове на банки",
      Path: "/admin/bankBranches",
      Icon: <ManageSearchOutlined />,
      UserTypes: [UserType.Admin],
    },
    Search: { Label: "Търси", Path: "/admin/bankBranches", Icon: <Search /> },
    New: { Label: "Добави", Path: "/admin/bankBranches/new", Icon: <AddBoxOutlined /> },
    Edit: { Label: "Редакция", Path: "/admin/bankBranches/edit/:id", Icon: <EditOutlined /> },
    Preview: { Label: "Преглед", Path: "/admin/bankBranches/preview/:id", Icon: <VisibilityOutlined /> },
  },
  BankAuthorizationLog: {
    Label: "Пълномощни банки",
    Path: "/admin/bankAuthorizations",
    Icon: <FileDownloadOutlined />,
    UserTypes: [UserType.Admin],
  },
  BankWithdrawnAuthorizationLog: {
    Label: "Оттеглени пълномощни банки",
    Path: "/admin/bankWithdrawnAuthorizations",
    Icon: <FileDownloadOutlined />,
    UserTypes: [UserType.Admin],
  },
  InvestmentBroker: {
    Root: {
      Label: "Инв. дружества",
      Path: "/admin/investmentBrokers",
      Icon: <ManageSearchOutlined />,
      UserTypes: [UserType.Admin],
    },
    Search: { Label: "Търси", Path: "/admin/investmentBrokers", Icon: <Search /> },
    New: { Label: "Добави", Path: "/admin/investmentBrokers/new", Icon: <AddBoxOutlined /> },
    Edit: { Label: "Редакция", Path: "/admin/investmentBrokers/edit/:id", Icon: <EditOutlined /> },
    Preview: { Label: "Преглед", Path: "/admin/investmentBrokers/preview/:id", Icon: <VisibilityOutlined /> },
  },
  InvestmentBrokerBranch: {
    Root: {
      Label: "Клонове инв. дружества",
      Path: "/admin/investmentBrokerBranches",
      Icon: <ManageSearchOutlined />,
      UserTypes: [UserType.Admin],
    },
    Search: { Label: "Търси", Path: "/admin/investmentBrokerBranches", Icon: <Search /> },
    New: { Label: "Добави", Path: "/admin/investmentBrokerBranches/new", Icon: <AddBoxOutlined /> },
    Edit: { Label: "Редакция", Path: "/admin/investmentBrokerBranches/edit/:id", Icon: <EditOutlined /> },
    Preview: { Label: "Преглед", Path: "/admin/investmentBrokerBranches/preview/:id", Icon: <VisibilityOutlined /> },
  },
  Insurer: {
    Root: {
      Label: "Застр. дружества",
      Path: "/admin/insurers",
      Icon: <ManageSearchOutlined />,
      UserTypes: [UserType.Admin],
    },
    Search: { Label: "Търси", Path: "/admin/insurers", Icon: <Search /> },
    New: { Label: "Добави", Path: "/admin/insurers/new", Icon: <AddBoxOutlined /> },
    Edit: { Label: "Редакция", Path: "/admin/insurers/edit/:id", Icon: <EditOutlined /> },
    Preview: { Label: "Преглед", Path: "/admin/insurers/preview/:id", Icon: <VisibilityOutlined /> },
  },
  InsurerBranch: {
    Root: {
      Label: "Клонове застр. дружества",
      Path: "/admin/insurerBranches",
      Icon: <ManageSearchOutlined />,
      UserTypes: [UserType.Admin],
    },
    Search: { Label: "Търси", Path: "/admin/insurerBranches", Icon: <Search /> },
    New: { Label: "Добави", Path: "/admin/insurerBranches/new", Icon: <AddBoxOutlined /> },
    Edit: { Label: "Редакция", Path: "/admin/insurerBranches/edit/:id", Icon: <EditOutlined /> },
    Preview: { Label: "Преглед", Path: "/admin/insurerBranches/preview/:id", Icon: <VisibilityOutlined /> },
  },
  InvestmentBrokerAuthorizationLog: {
    Label: "Пълномощни инв. посредници",
    Path: "/admin/investmentBrokerAuthorizations",
    Icon: <FileDownloadOutlined />,
    UserTypes: [UserType.Admin],
  },
  InvestmentBrokerWithdrawnAuthorizationLog: {
    Label: "Оттеглени пълномощни инв. посредници",
    Path: "/admin/investmentBrokerWithdrawnAuthorizations",
    Icon: <FileDownloadOutlined />,
    UserTypes: [UserType.Admin],
  },
  InsurerAuthorizationLog: {
    Label: "Пълномощни застрахователи",
    Path: "/admin/insurerAuthorizations",
    Icon: <FileDownloadOutlined />,
    UserTypes: [UserType.Admin],
  },
  InsurerWithdrawnAuthorizationLog: {
    Label: "Оттеглени пълномощни застрахователи",
    Path: "/admin/insurerWithdrawnAuthorizations",
    Icon: <FileDownloadOutlined />,
    UserTypes: [UserType.Admin],
  },
  NapObligationLog: {
    Label: "Проверки на задължено лице",
    Path: "/admin/obligationRequests",
    Icon: <FileDownloadOutlined />,
    UserTypes: [UserType.Admin],
  },
};
